import React, { useEffect, useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import {
  CalculateForecastRequest,
  CreateDemandForecastRequest,
  DemandForecast,
  ForecastMethod,
  ForecastPeriod,
  ForecastMethodLabels,
  ForecastPeriodLabels,
  GenerateForecastsRequest,
} from '../../types/demand-forecasting.types';
import { inventoryService } from '../../services/inventory.service';
import { useForecastCalculation } from '../../hooks/useDemandForecasting';
import {
  Field,
  formatNumber,
  inputClass,
  inputErrorClass,
  primaryButtonClass,
  secondaryButtonClass,
} from '../ui/FormControls';
import { cn } from '../../lib/utils';

// One saved forecast, or a run of forecasts for several periods from the same history
export type DemandForecastSubmission =
  | { kind: 'single'; data: CreateDemandForecastRequest }
  | { kind: 'series'; data: GenerateForecastsRequest };

interface DemandForecastFormProps {
  // When set, the form edits this forecast; otherwise it creates a new one
  forecast?: DemandForecast | null;
  onSubmit: (submission: DemandForecastSubmission) => Promise<void>;
  onCancel: () => void;
  isSaving: boolean;
}

const MAX_DEMAND = 99999999.99;
const MAX_HISTORY_POINTS = 120;
const MAX_PERIODS = 24;

export const METHOD_HINTS: Record<ForecastMethod, string> = {
  [ForecastMethod.MOVING_AVERAGE]: 'The average of the most recent periods.',
  [ForecastMethod.EXPONENTIAL_SMOOTHING]: 'A weighted average that favours recent periods.',
  [ForecastMethod.LINEAR_REGRESSION]: 'Continues the straight-line trend in the history.',
  [ForecastMethod.SEASONAL_NAIVE]: 'Repeats the value from the same point one season earlier.',
};

// Periods in a typical season, used as the starting season length
const DEFAULT_SEASON: Record<ForecastPeriod, string> = {
  [ForecastPeriod.DAILY]: '7',
  [ForecastPeriod.WEEKLY]: '52',
  [ForecastPeriod.MONTHLY]: '12',
  [ForecastPeriod.QUARTERLY]: '4',
};

// Forecast dates are stored as midnight UTC, so they are shown in UTC to keep the day
export const formatForecastDate = (value?: string | null) =>
  value
    ? new Date(value).toLocaleDateString('en-IN', {
        day: '2-digit',
        month: 'short',
        year: 'numeric',
        timeZone: 'UTC',
      })
    : '—';

const firstOfNextMonth = () => {
  const now = new Date();
  const next = new Date(now.getFullYear(), now.getMonth() + 1, 1);
  return `${next.getFullYear()}-${String(next.getMonth() + 1).padStart(2, '0')}-01`;
};

// Numbers separated by commas, spaces or new lines; null when something is not a valid quantity
const parseHistory = (text: string): number[] | null => {
  const parts = text.split(/[\s,;]+/).filter(Boolean);
  const values = parts.map(Number);
  return values.every((value) => Number.isFinite(value) && value >= 0 && value <= MAX_DEMAND) ? values : null;
};

// A numbered group of related fields
const Section: React.FC<{
  step: number;
  title: string;
  description: string;
  children: React.ReactNode;
}> = ({ step, title, description, children }) => (
  <section className="rounded-lg border border-gray-200 bg-white">
    <div className="flex items-center gap-3 rounded-t-lg border-b border-gray-200 bg-gray-50 px-4 py-3">
      <span className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-indigo-600 text-xs font-semibold text-white">
        {step}
      </span>
      <div>
        <h4 className="text-sm font-semibold text-gray-900">{title}</h4>
        <p className="mt-0.5 text-xs text-gray-500">{description}</p>
      </div>
    </div>
    <div className="grid grid-cols-1 gap-x-4 gap-y-4 p-4 sm:grid-cols-2">{children}</div>
  </section>
);

const DemandForecastForm: React.FC<DemandForecastFormProps> = ({ forecast, onSubmit, onCancel, isSaving }) => {
  const isEdit = !!forecast;

  const [form, setForm] = useState({
    productId: forecast?.productId || '',
    period: forecast?.period || ForecastPeriod.MONTHLY,
    forecastDate: forecast ? String(forecast.forecastDate).slice(0, 10) : firstOfNextMonth(),
    status: forecast && !forecast.isActive ? 'inactive' : 'active',
    method: forecast?.method || ForecastMethod.MOVING_AVERAGE,
    periodLookback: String(forecast?.periodLookback ?? 3),
    smoothingFactor: String(forecast?.smoothingFactor ?? 0.3),
    historicalData: (forecast?.historicalData || []).join(', '),
    forecastedDemand: forecast ? String(forecast.forecastedDemand) : '',
    periods: '1',
    actualDemand: forecast?.actualDemand != null ? String(forecast.actualDemand) : '',
    notes: forecast?.notes || '',
  });
  const [errors, setErrors] = useState<Record<string, string>>({});

  const { data: products = [], isLoading: productsLoading } = useQuery({
    queryKey: ['products'],
    queryFn: () => inventoryService.getProducts(),
  });

  const setField = (name: keyof typeof form, value: string) => {
    setForm((prev) => {
      const next = { ...prev, [name]: value };
      // The lookback means "season length" for seasonal naive, so it gets a fitting start value
      if (name === 'method' && value !== prev.method) {
        if (value === ForecastMethod.SEASONAL_NAIVE) next.periodLookback = DEFAULT_SEASON[prev.period];
        else if (prev.method === ForecastMethod.SEASONAL_NAIVE) next.periodLookback = '3';
      }
      return next;
    });
    if (errors[name]) {
      setErrors((prev) => ({ ...prev, [name]: '' }));
    }
  };

  const history = useMemo(() => parseHistory(form.historicalData), [form.historicalData]);
  const hasHistory = !!history && history.length > 0;
  const usesLookback =
    form.method === ForecastMethod.MOVING_AVERAGE || form.method === ForecastMethod.SEASONAL_NAIVE;
  const usesSmoothing = form.method === ForecastMethod.EXPONENTIAL_SMOOTHING;

  const lookback = Number(form.periodLookback);
  const smoothing = Number(form.smoothingFactor);
  const periods = isEdit ? 1 : Number(form.periods);
  const lookbackValid = Number.isInteger(lookback) && lookback >= 1 && lookback <= 52;
  const smoothingValid = smoothing >= 0.1 && smoothing <= 0.9;
  const periodsValid = Number.isInteger(periods) && periods >= 1 && periods <= MAX_PERIODS;
  const isSeries = hasHistory && periodsValid && periods > 1;

  // What the server would calculate from the history, shown before anything is saved
  const calculation: CalculateForecastRequest | null =
    hasHistory &&
    history.length <= MAX_HISTORY_POINTS &&
    periodsValid &&
    (!usesLookback || lookbackValid) &&
    (!usesSmoothing || smoothingValid)
      ? {
          method: form.method,
          historicalData: history,
          periodLookback: lookbackValid ? lookback : 3,
          smoothingFactor: smoothingValid ? smoothing : 0.3,
          periods,
          period: form.period,
          ...(form.forecastDate ? { startDate: form.forecastDate } : {}),
        }
      : null;

  // Wait for a pause in typing before asking the server
  const calculationKey = JSON.stringify(calculation);
  const [debouncedKey, setDebouncedKey] = useState(calculationKey);
  useEffect(() => {
    const timer = setTimeout(() => setDebouncedKey(calculationKey), 300);
    return () => clearTimeout(timer);
  }, [calculationKey]);
  const debouncedCalculation = useMemo(
    () => JSON.parse(debouncedKey) as CalculateForecastRequest | null,
    [debouncedKey],
  );

  const { data: calculated, isFetching: isCalculating } = useForecastCalculation(debouncedCalculation);
  const calculatedForecasts = calculation && calculated ? calculated : [];
  const calculatedDemand = calculatedForecasts[0]?.forecastedDemand;

  const selectedProduct = products.find((product) => product.id === form.productId);
  const unit = selectedProduct?.unit || 'units';

  const validate = () => {
    const next: Record<string, string> = {};

    if (!form.productId) next.productId = 'Product is required';
    if (!form.forecastDate) next.forecastDate = 'Forecast date is required';

    if (usesLookback && !lookbackValid) {
      next.periodLookback = 'Enter a whole number between 1 and 52';
    }
    if (usesSmoothing && !smoothingValid) {
      next.smoothingFactor = 'Smoothing factor must be between 0.1 and 0.9';
    }

    if (!history) {
      next.historicalData = 'Use only numbers of 0 or more, separated by commas or spaces';
    } else if (history.length > MAX_HISTORY_POINTS) {
      next.historicalData = `Enter at most ${MAX_HISTORY_POINTS} periods of history`;
    }

    if (history && history.length === 0) {
      const demand = Number(form.forecastedDemand);
      if (form.forecastedDemand.trim() === '' || !Number.isFinite(demand) || demand < 0) {
        next.forecastedDemand = 'Enter the forecasted demand, or add history to calculate it';
      } else if (demand > MAX_DEMAND) {
        next.forecastedDemand = 'Forecasted demand is too large';
      }
    }

    if (!isEdit && hasHistory && !periodsValid) {
      next.periods = `Enter a whole number between 1 and ${MAX_PERIODS}`;
    }

    if (form.actualDemand.trim() !== '' && !isSeries) {
      const actual = Number(form.actualDemand);
      if (!Number.isFinite(actual) || actual < 0) next.actualDemand = 'Actual demand must be 0 or more';
      else if (actual > MAX_DEMAND) next.actualDemand = 'Actual demand is too large';
    }

    if (form.notes.length > 1000) next.notes = 'Notes can be at most 1000 characters';

    setErrors(next);
    return Object.keys(next).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!validate() || !history) return;

    const isActive = form.status === 'active';
    const notes = form.notes.trim();
    const parameters = {
      periodLookback: lookbackValid ? lookback : 3,
      smoothingFactor: smoothingValid ? smoothing : 0.3,
    };

    let submission: DemandForecastSubmission;
    if (isSeries) {
      submission = {
        kind: 'series',
        data: {
          productId: form.productId,
          historicalSalesData: history,
          periods,
          method: form.method,
          period: form.period,
          startDate: form.forecastDate,
          ...parameters,
          isActive,
          ...(notes ? { notes } : {}),
        },
      };
    } else {
      submission = {
        kind: 'single',
        data: {
          productId: form.productId,
          method: form.method,
          period: form.period,
          forecastDate: form.forecastDate,
          ...parameters,
          isActive,
          // On an edit, null clears a value that was removed in the form
          notes: notes || (isEdit ? null : undefined),
          actualDemand: form.actualDemand.trim() !== '' ? Number(form.actualDemand) : isEdit ? null : undefined,
          ...(hasHistory
            ? { historicalData: history }
            : { historicalData: isEdit ? null : undefined, forecastedDemand: Number(form.forecastedDemand) }),
        },
      };
    }

    try {
      await onSubmit(submission);
    } catch (error) {
      // The caller reports the failure; keep the form open so nothing is lost
    }
  };

  const fieldClass = (key: string) => cn(inputClass, errors[key] && inputErrorClass);
  const errorCount = Object.values(errors).filter(Boolean).length;

  const shownDemand = hasHistory ? calculatedDemand : form.forecastedDemand.trim() === '' ? undefined : Number(form.forecastedDemand);
  const submitLabel = isEdit ? 'Update Forecast' : isSeries ? `Create ${periods} Forecasts` : 'Create Forecast';

  return (
    <form onSubmit={handleSubmit} noValidate>
      <div className="grid grid-cols-1 gap-5 xl:grid-cols-3">
        <div className="space-y-5 xl:col-span-2">
          <Section step={1} title="Product and period" description="What is being forecast, and for when.">
            <Field
              label="Product"
              htmlFor="df-product"
              required
              error={errors.productId}
              className="sm:col-span-2"
            >
              <select
                id="df-product"
                value={form.productId}
                onChange={(e) => setField('productId', e.target.value)}
                className={fieldClass('productId')}
              >
                <option value="">{productsLoading ? 'Loading products...' : 'Select a product'}</option>
                {/* Keeps the product of an old forecast selectable even if it is no longer in the list */}
                {forecast && !productsLoading && !products.some((product) => product.id === forecast.productId) && (
                  <option value={forecast.productId}>
                    {forecast.productName} - {forecast.productSku}
                  </option>
                )}
                {products.map((product) => (
                  <option key={product.id} value={product.id}>
                    {product.name} - {product.sku}
                  </option>
                ))}
              </select>
            </Field>

            <Field label="Forecast Period" htmlFor="df-period" hint="The length of time one forecast covers.">
              <select
                id="df-period"
                value={form.period}
                onChange={(e) => setField('period', e.target.value)}
                className={inputClass}
              >
                {Object.values(ForecastPeriod).map((period) => (
                  <option key={period} value={period}>
                    {ForecastPeriodLabels[period]}
                  </option>
                ))}
              </select>
            </Field>

            <Field
              label="Forecast Date"
              htmlFor="df-date"
              required
              error={errors.forecastDate}
              hint={isSeries ? 'The first period; the others follow from it.' : 'The start of the period being forecast.'}
            >
              <input
                id="df-date"
                type="date"
                value={form.forecastDate}
                onChange={(e) => setField('forecastDate', e.target.value)}
                className={fieldClass('forecastDate')}
              />
            </Field>

            <Field label="Status" htmlFor="df-status" hint="Inactive forecasts are kept for reference only.">
              <select
                id="df-status"
                value={form.status}
                onChange={(e) => setField('status', e.target.value)}
                className={inputClass}
              >
                <option value="active">Active</option>
                <option value="inactive">Inactive</option>
              </select>
            </Field>
          </Section>

          <Section step={2} title="Method" description="How demand is calculated from the history.">
            <Field label="Forecasting Method" htmlFor="df-method" hint={METHOD_HINTS[form.method]}>
              <select
                id="df-method"
                value={form.method}
                onChange={(e) => setField('method', e.target.value)}
                className={inputClass}
              >
                {Object.values(ForecastMethod).map((method) => (
                  <option key={method} value={method}>
                    {ForecastMethodLabels[method]}
                  </option>
                ))}
              </select>
            </Field>

            {usesLookback && (
              <Field
                label={form.method === ForecastMethod.SEASONAL_NAIVE ? 'Season Length' : 'Periods to Average'}
                htmlFor="df-lookback"
                error={errors.periodLookback}
                hint={
                  form.method === ForecastMethod.SEASONAL_NAIVE
                    ? 'Periods in one season, e.g. 12 for monthly data. Needs that much history.'
                    : 'How many of the latest periods are averaged (1-52).'
                }
              >
                <input
                  id="df-lookback"
                  type="number"
                  min="1"
                  max="52"
                  step="1"
                  value={form.periodLookback}
                  onChange={(e) => setField('periodLookback', e.target.value)}
                  className={fieldClass('periodLookback')}
                />
              </Field>
            )}

            {usesSmoothing && (
              <Field
                label="Smoothing Factor"
                htmlFor="df-smoothing"
                error={errors.smoothingFactor}
                hint="0.1 to 0.9. Higher follows recent change more closely."
              >
                <input
                  id="df-smoothing"
                  type="number"
                  min="0.1"
                  max="0.9"
                  step="0.05"
                  value={form.smoothingFactor}
                  onChange={(e) => setField('smoothingFactor', e.target.value)}
                  className={fieldClass('smoothingFactor')}
                />
              </Field>
            )}
          </Section>

          <Section step={3} title="Demand figures" description="Past demand, the forecast and the actual result.">
            <Field
              label="Historical Demand"
              htmlFor="df-history"
              error={errors.historicalData}
              hint="One number per period, oldest first, separated by commas or spaces. When filled in, the forecast is calculated from it."
              className="sm:col-span-2"
            >
              <textarea
                id="df-history"
                rows={3}
                value={form.historicalData}
                onChange={(e) => setField('historicalData', e.target.value)}
                className={cn(fieldClass('historicalData'), 'font-mono')}
                placeholder="e.g. 100, 120, 95, 110"
              />
            </Field>

            <Field
              label="Forecasted Demand"
              htmlFor="df-demand"
              required={!hasHistory}
              error={errors.forecastedDemand}
              hint={hasHistory ? 'Calculated from the history with the chosen method.' : 'Enter it by hand, or add history above.'}
            >
              {hasHistory ? (
                <input
                  id="df-demand"
                  type="text"
                  readOnly
                  value={calculatedDemand !== undefined ? formatNumber(calculatedDemand, 2) : isCalculating ? 'Calculating...' : ''}
                  className={inputClass}
                />
              ) : (
                <input
                  id="df-demand"
                  type="number"
                  min="0"
                  step="0.01"
                  value={form.forecastedDemand}
                  onChange={(e) => setField('forecastedDemand', e.target.value)}
                  className={fieldClass('forecastedDemand')}
                  placeholder="0"
                />
              )}
            </Field>

            {!isEdit && (
              <Field
                label="Periods to Forecast"
                htmlFor="df-periods"
                error={errors.periods}
                hint={
                  hasHistory
                    ? `Above 1 creates one forecast per period (up to ${MAX_PERIODS}).`
                    : 'Add history to forecast several periods at once.'
                }
              >
                <input
                  id="df-periods"
                  type="number"
                  min="1"
                  max={MAX_PERIODS}
                  step="1"
                  disabled={!hasHistory}
                  value={hasHistory ? form.periods : '1'}
                  onChange={(e) => setField('periods', e.target.value)}
                  className={fieldClass('periods')}
                />
              </Field>
            )}

            <Field
              label="Actual Demand"
              htmlFor="df-actual"
              error={errors.actualDemand}
              hint={
                isSeries
                  ? 'Record actuals on each forecast after it is created.'
                  : 'Leave empty until the period is over; it sets the accuracy.'
              }
            >
              <input
                id="df-actual"
                type="number"
                min="0"
                step="0.01"
                disabled={isSeries}
                value={isSeries ? '' : form.actualDemand}
                onChange={(e) => setField('actualDemand', e.target.value)}
                className={fieldClass('actualDemand')}
                placeholder="Not known yet"
              />
            </Field>
          </Section>
        </div>

        {/* Right: the result so far, and notes */}
        <aside className="xl:col-span-1">
          <div className="space-y-5 xl:sticky xl:top-4">
            <section className="rounded-lg border border-gray-200 bg-white">
              <h4 className="rounded-t-lg border-b border-gray-200 bg-gray-50 px-4 py-3 text-sm font-semibold text-gray-900">
                Summary
              </h4>
              <div className="space-y-4 p-4 text-sm">
                <div>
                  <p className="text-xs text-gray-500">Product</p>
                  <p className="font-medium text-gray-900">
                    {selectedProduct?.name || (isEdit && forecast?.productId === form.productId ? forecast.productName : 'Not selected yet')}
                  </p>
                  {selectedProduct && <p className="font-mono text-xs text-gray-500">{selectedProduct.sku}</p>}
                </div>

                <div className="rounded-md bg-indigo-50 px-3 py-3">
                  <p className="text-xs text-indigo-700">
                    {isSeries ? 'Forecast for the first period' : 'Forecasted demand'}
                  </p>
                  <p className="text-2xl font-semibold text-indigo-900 tabular-nums">
                    {shownDemand !== undefined && Number.isFinite(shownDemand) ? formatNumber(shownDemand, 2) : '—'}
                    {shownDemand !== undefined && Number.isFinite(shownDemand) && (
                      <span className="ml-1 text-sm font-medium text-indigo-700">{unit}</span>
                    )}
                  </p>
                  <p className="mt-0.5 text-xs text-indigo-700">
                    {hasHistory
                      ? `${ForecastMethodLabels[form.method]} of ${history.length} ${history.length === 1 ? 'period' : 'periods'}`
                      : 'Entered by hand'}
                  </p>
                </div>

                <dl className="space-y-2">
                  <div className="flex justify-between gap-3">
                    <dt className="text-gray-600">Period</dt>
                    <dd className="font-medium text-gray-900">{ForecastPeriodLabels[form.period]}</dd>
                  </div>
                  <div className="flex justify-between gap-3">
                    <dt className="text-gray-600">{isSeries ? 'Starts' : 'Forecast date'}</dt>
                    <dd className="font-medium text-gray-900">{formatForecastDate(form.forecastDate)}</dd>
                  </div>
                  <div className="flex justify-between gap-3">
                    <dt className="text-gray-600">Status</dt>
                    <dd className="font-medium text-gray-900">{form.status === 'active' ? 'Active' : 'Inactive'}</dd>
                  </div>
                </dl>

                {isSeries && calculatedForecasts.length > 1 && (
                  <div>
                    <p className="text-xs font-semibold uppercase tracking-wide text-gray-500">
                      Forecasts to create
                    </p>
                    <ul className="mt-2 max-h-56 divide-y divide-gray-100 overflow-y-auto rounded-md border border-gray-200">
                      {calculatedForecasts.map((item, index) => (
                        <li key={index} className="flex justify-between gap-3 px-3 py-1.5">
                          <span className="text-gray-600">
                            {item.forecastDate ? formatForecastDate(item.forecastDate) : `Period ${index + 1}`}
                          </span>
                          <span className="font-medium text-gray-900 tabular-nums">
                            {formatNumber(item.forecastedDemand, 2)}
                          </span>
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
              </div>
            </section>

            <section className="rounded-lg border border-gray-200 bg-white p-4">
              <Field label="Notes" htmlFor="df-notes" error={errors.notes}>
                <textarea
                  id="df-notes"
                  rows={3}
                  value={form.notes}
                  onChange={(e) => setField('notes', e.target.value)}
                  className={fieldClass('notes')}
                  placeholder="Assumptions, promotions, anything that explains this forecast"
                />
              </Field>
            </section>
          </div>
        </aside>
      </div>

      {/* Footer keeps the problems count in view next to the buttons */}
      <div className="-mx-6 -mb-4 mt-6 flex flex-col gap-3 rounded-b-lg border-t border-gray-200 bg-gray-50 px-6 py-4 sm:flex-row sm:items-center sm:justify-between">
        <div className="text-sm">
          {errorCount > 0 ? (
            <span className="font-medium text-red-600">
              {errorCount} {errorCount === 1 ? 'field needs' : 'fields need'} attention
            </span>
          ) : (
            <span className="text-gray-600">
              {isSeries
                ? `${periods} ${ForecastPeriodLabels[form.period].toLowerCase()} forecasts will be created`
                : hasHistory
                  ? 'The forecast is calculated from the history'
                  : 'The forecast is entered by hand'}
            </span>
          )}
        </div>
        <div className="flex flex-col-reverse gap-3 sm:flex-row">
          <button type="button" onClick={onCancel} disabled={isSaving} className={secondaryButtonClass}>
            Cancel
          </button>
          <button type="submit" disabled={isSaving} className={primaryButtonClass}>
            {isSaving ? (isEdit ? 'Updating...' : 'Creating...') : submitLabel}
          </button>
        </div>
      </div>
    </form>
  );
};

export default DemandForecastForm;
